import { useCallback, useEffect, useMemo } from 'react';
import { useIsMobileOrTablet } from '@shared/utils/media/useMediaScreens';
import { useCatalogProductListData } from '@apps/registry/common/components/Catalog/hooks/useCatalogProductListData/useCatalogProductListData';
import { deriveTelemetryPage, PagesForTelemetry, useCatalogContext } from '@apps/registry/common/components/Catalog';
import { DeriveTelemetryPageArgs } from '@apps/registry/common/components/Catalog/utils/deriveTelemetryPage';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { OnClickForCashDialogFn } from '@apps/registry/common/components/Catalog/components/ProductGrid/ProductGrid.types';
import { useCashFundDialogContext } from '@apps/registry/common/components/Catalog/components/CashFundDialogProvider';
import { CATEGORY_TITLE_TO_FUND_TYPE, CategoryTitleToFundTypeKey } from '@apps/registry/common/components/Catalog/components/ProductGrid/ProductGrid.controller';
import { ProductCatalogStorefrontStatus } from '@graphql/generated';
import { useCatalogLoggingContext } from '@apps/registry/common/components/Catalog/Catalog.logging';
import { DonationFundItemType } from '@apps/registry/common/components/Catalog/utils/interpretItemType';
import { isCatalogItem } from '../../../../Catalog.types';

interface CategoryRecommendationsCarouselControllerProps {
  isOnScreen: boolean;
  category: {
    id: string;
    title: string;
    publicUrlPath: string;
  };
}

export const useCategoryRecommendationsCarouselController = (props: CategoryRecommendationsCarouselControllerProps) => {
  const { eventId, isMobile, addCatalogItemToRegistry, addCatalogItemLoading, selectedCategory, onAddItemToRegistryWithoutEvent, goToCatalogItem } = useCatalogContext();

  const { openPreviewAndEditCatalogCashItemDialog, openAddCatalogCashFundDialog } = useCashFundDialogContext();

  const { catalogProductsIndexQueryError } = useCatalogLoggingContext();

  const {
    isOnScreen,
    category: { id, title, publicUrlPath },
  } = props;

  const isMobileOrTablet = useIsMobileOrTablet();

  const { data, loading, productList, totalProductCount, error, hasNextPage, isFetchingMore, fallbackCatalogProductList, fallbackCatalogDataError, loadingFallbackCatalog } =
    useCatalogProductListData({
      pageSize: 20,
      selectedCategory: null,
      searchTerm: null,
      telemetryPageValue: 'category',
      skip: !isOnScreen,
      url: publicUrlPath,
      shouldApplyFilters: false,
    });

  const deriveTelemetryPageForProduct = useCallback((args: DeriveTelemetryPageArgs): PagesForTelemetry => {
    return deriveTelemetryPage(args);
  }, []);

  useEffect(() => {
    const apolloError = error || fallbackCatalogDataError;
    if (apolloError) {
      catalogProductsIndexQueryError({ error: apolloError });
    }
  }, [error, catalogProductsIndexQueryError, fallbackCatalogDataError]);

  const shouldRevealCarousel = !!data && !(loading && !productList.length);

  const fundType = CATEGORY_TITLE_TO_FUND_TYPE[selectedCategory?.title as CategoryTitleToFundTypeKey] || '';

  const handleClickForCashDialog = useEventCallback<OnClickForCashDialogFn>(({ clickedProduct, isQuickAdd, itemPosition }) => {
    if (eventId) {
      if (isQuickAdd) {
        openAddCatalogCashFundDialog({ addMethodType: 'quick add', product: clickedProduct, itemPosition });
      } else {
        openPreviewAndEditCatalogCashItemDialog({ product: clickedProduct, itemPosition });
      }
    } else {
      onAddItemToRegistryWithoutEvent?.({
        type: 'catalog',
        itemType: (clickedProduct.itemType || fundType) as DonationFundItemType,
        catalogLocator: null,
        curation: null,
        category: selectedCategory,
        product: clickedProduct,
        isCreateFromCustom: false,
        itemPosition,
        telemetryArgs: {
          method: isQuickAdd ? 'quick add' : 'pdp cta',
          page: deriveTelemetryPageForProduct({ view: 'productGrid', product: clickedProduct, selectedCategory }),
        },
      });
    }
  });

  // checks if at least one product of the list has 'comingSoon' storefront status
  const hasAnyStorefrontWarning = useMemo<boolean>(() => {
    return productList.some(product => isCatalogItem(product) && product.storefrontInfo.storefrontStatus === ProductCatalogStorefrontStatus.comingSoon);
  }, [productList]);

  const wrappedAddCatalogItem = useCallback<typeof addCatalogItemToRegistry>(
    ({ source, payload }) => {
      if (source !== 'universalSearch') {
        const { telemetryArgs, ...restPayload } = payload;
        addCatalogItemToRegistry({
          source,
          payload: {
            ...restPayload,
            telemetryArgs: {
              ...telemetryArgs,
              component: 'categoryRec',
              sectionCategoryId: id,
              sectionTitle: title,
            },
          },
        });
      }
    },
    [addCatalogItemToRegistry, id, title],
  );

  return {
    productList,
    fallbackCatalogProductList,
    eventId,
    isMobile,
    hasLoadingError: !!error || !!fallbackCatalogDataError,
    addCatalogItemToRegistry: wrappedAddCatalogItem,
    addCatalogItemLoading,
    selectedCategory,
    loading: !!loading || !!loadingFallbackCatalog,
    showSkeleton: !shouldRevealCarousel || !!error || !!fallbackCatalogDataError || !!loadingFallbackCatalog,
    isMobileOrTablet,
    hasNextPage,
    isFetchingMore,
    totalProductCount,
    deriveTelemetryPageForProduct,
    handleClickForCashDialog,
    hasAnyStorefrontWarning,
    goToCatalogItem,
  };
};
